import { patchHTML } from "../core/surface-host.js";
import { ensureAdminContentLayout } from "./layout.js";
import { escapeHTML, renderSkeleton, renderTruncatedID } from "../render/ui-helpers.js";
import { LLM_THREAD_CAPABILITY_OPTIONS } from "../inference-capabilities.js";
import { renderHarnessAgentModeSection } from "./agent-form/harness.js";
import { renderLLMAgentModeSection } from "./agent-form/llm.js";

export function renderAgentsRoute(layoutRoot, state, options = {}) {
  ensureAdminContentLayout(layoutRoot, "agents", `
    <div class="entity-page">
      <div data-agents-form-root></div>
      <div data-agents-list-root></div>
    </div>
  `);
  const formRoot = layoutRoot.querySelector("[data-agents-form-root]");
  const listRoot = layoutRoot.querySelector("[data-agents-list-root]");
  if (!state.apiKey) {
    patchHTML(formRoot, "");
    patchHTML(listRoot, options.renderLockedState?.() || "");
    return;
  }
  if (state.loading) {
    patchHTML(formRoot, "");
    patchHTML(listRoot, renderSkeleton(4));
    return;
  }
  const pageState = buildAgentsPageState(state, options);
  patchHTML(formRoot, renderAgentsFormSection(state, pageState, options));
  patchHTML(listRoot, renderAgentsListSection(state, options));
}

export function buildAgentsPageState(state, options = {}) {
  const editingAgent = state.editingAgentID ? options.lookupByID?.(state.agents, state.editingAgentID) || null : null;
  const isEditing = Boolean(editingAgent);
  const showForm = state.agentCreateOpen || isEditing;
  const agentDraft = state.forms.agent;
  const availableHarnesses = options.harnessesForAdapter?.(agentDraft.adapter_id) || [];
  const selectedHarness = options.lookupHarnessForDraft?.(agentDraft) || null;
  const hasHarnessChoice = agentDraft.adapter_id === "opencode.server" && availableHarnesses.length > 1;
  const staleServerURL = options.staleAttachedServerURL?.(agentDraft) || "";
  const allLLMRoutes = Array.isArray(state.inferenceRoutes) ? state.inferenceRoutes : [];
  const llmCapabilitySections = LLM_THREAD_CAPABILITY_OPTIONS.map((item) => {
    const capability = item.value;
    const defaults = agentDraft.capability_defaults?.[capability] || { route_id: "", model: "" };
    const selectedRoute = options.lookupByID?.(allLLMRoutes, defaults.route_id) || null;
    const availableRoutes = allLLMRoutes.filter((route) =>
      route.capability_family === capability && (route.enabled || route.id === defaults.route_id)
    );
    const routeModels = selectedRoute ? uniqueStrings([
      selectedRoute.default_model,
      ...(Array.isArray(selectedRoute.advertised_models) ? selectedRoute.advertised_models : [])
    ]) : [];
    return {
      capability,
      label: item.label,
      routeFieldName: `capability_defaults.${capability}.route_id`,
      modelFieldName: `capability_defaults.${capability}.model`,
      defaults,
      availableRoutes,
      selectedRoute,
      routeModels
    };
  });
  return {
    editingAgent,
    isEditing,
    showForm,
    agentDraft,
    availableHarnesses,
    selectedHarness,
    hasHarnessChoice,
    staleServerURL,
    llmCapabilitySections
  };
}

export function renderAgentsFormSection(state, pageState, options = {}) {
  if (!pageState.showForm) {
    return "";
  }
  return `
    <div class="entity-form-panel">
      ${pageState.isEditing ? `<div class="form-note">Editing <strong>${escapeHTML(pageState.editingAgent.name)}</strong> ${renderTruncatedID(pageState.editingAgent.id)}</div>` : ""}
      <form id="agent-form" class="form-grid">
        <div class="form-grid form-grid--split">
          <div class="field">
            <label for="agent-name">Name</label>
            <input id="agent-name" name="name" required placeholder="Review bot" value="${escapeHTML(pageState.agentDraft.name)}">
          </div>
          <div class="field">
            <label for="agent-credential-ref">Credential ref</label>
            <input id="agent-credential-ref" name="credential_ref" placeholder="OPENAI_API_KEY" value="${escapeHTML(pageState.agentDraft.credential_ref)}">
          </div>
        </div>
        <div class="field">
          <label for="agent-execution-mode">Mode</label>
          <select id="agent-execution-mode" name="execution_mode">
            <option value="harness" ${pageState.agentDraft.execution_mode === "harness" ? "selected" : ""}>Harness</option>
            <option value="llm" ${pageState.agentDraft.execution_mode === "llm" ? "selected" : ""}>LLM</option>
          </select>
        </div>
        ${renderAgentModeSection(pageState, options)}
        <details class="disclosure">
          <summary>Advanced settings</summary>
          <div class="disclosure__body" style="display:grid; gap:var(--space-md); padding-top:var(--space-sm);">
            <div class="field">
              <label for="agent-system-prompt">System prompt</label>
              <textarea id="agent-system-prompt" name="system_prompt" placeholder="Optional operator prompt">${escapeHTML(pageState.agentDraft.system_prompt)}</textarea>
            </div>
            <div class="field">
              <label for="agent-runtime-config">Runtime config overrides</label>
              <textarea id="agent-runtime-config" name="runtime_config" placeholder='{"username": "demo"}'>${escapeHTML(pageState.agentDraft.runtime_config)}</textarea>
            </div>
          </div>
        </details>
        ${state.formErrors.agent ? `<div class="form-error">${escapeHTML(state.formErrors.agent)}</div>` : ""}
        <div class="button-row">
          <button class="button button--primary" type="submit">${pageState.isEditing ? "Save" : "Create agent"}</button>
          ${pageState.isEditing ? `<button class="button button--ghost" type="button" data-action="cancel-agent-edit">Cancel</button>` : ""}
        </div>
      </form>
    </div>
  `;
}

function renderAgentModeSection(pageState, options) {
  if (pageState.agentDraft.execution_mode === "llm") {
    return renderLLMAgentModeSection(pageState, options);
  }
  return renderHarnessAgentModeSection(pageState, options);
}

function uniqueStrings(items) {
  const seen = new Set();
  const normalized = [];
  for (const item of items) {
    const value = String(item || "").trim();
    if (!value || seen.has(value)) {
      continue;
    }
    seen.add(value);
    normalized.push(value);
  }
  return normalized;
}

export function renderAgentsListSection(state, options = {}) {
  return `
    <div class="entity-list">
      ${state.agents.length ? state.agents.map((item) => {
        const harness = options.lookupByID?.(state.harnesses, item.harness_id) || null;
        const harnessLabel = harness ? options.harnessDisplayLabel?.(harness) || harness.id : "";
        return `
          <article class="entity-row">
            <div class="entity-row__main">
              <div class="entity-row__body">
                <div class="entity-row__title">${escapeHTML(item.name)}</div>
                <div class="entity-row__meta">${escapeHTML(item.execution_mode || "harness")} · ${escapeHTML(item.adapter_id || "llm backend")} · ${escapeHTML(item.model || "default model")}</div>
                ${harnessLabel || item.credential_ref ? `<div class="entity-row__detail">${[harnessLabel, item.credential_ref ? `cred: ${item.credential_ref}` : ""].filter(Boolean).map(escapeHTML).join(" · ")}</div>` : ""}
              </div>
              <div class="entity-row__actions">
                <button class="button button--sm button--ghost" type="button" data-action="edit-agent" data-agent-id="${escapeHTML(item.id)}">Edit</button>
                <button class="button button--sm button--danger" type="button" data-action="delete-agent" data-agent-id="${escapeHTML(item.id)}">Delete</button>
              </div>
            </div>
          </article>
        `;
      }).join("") : `
        <div class="entity-empty">
          <div class="entity-empty__title">No agents yet</div>
          <div class="entity-empty__copy">Create an agent to bind a model to your threads.</div>
          <button class="button button--primary" type="button" data-action="toggle-agent-create">+ New agent</button>
        </div>
      `}
    </div>
  `;
}
